import React, { FC, MutableRefObject, ReactElement, useRef } from 'react'
import { UserTour } from '../../../../i18n'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import Icon, { IconName } from '../../Common/Components/Icon'
import { WatsonButton } from '../../Common/Components/WatsonButton/WatsonButton'
import { TourEntry, TourOverlayPosition } from '../Types/Types'

type Props = {
    tourPosition: [TourOverlayPosition, DOMRect]
    tourEntry: TourEntry
    rect: DOMRect
    nextTour: () => void
    cancelTour: (turnOff?: boolean) => void
}

const GUTTER_DISTANCE: number = 25

export const TourOverlay: FC<Props> = ({
    tourPosition: [overlayPosition, overlayPositionRect],
    tourEntry: { disableNext, content },
    rect,
    nextTour,
    cancelTour
}: Props): ReactElement => {
    const language: CLanguageHandler = CLanguageHandler.getInstance()
    language.loadBundle(UserTour.NAME)

    const ref: MutableRefObject<OnlyNullable<HTMLDivElement>> = useRef<OnlyNullable<HTMLDivElement>>(null)

    const getTopPosition = (): number => {
        switch (overlayPosition) {
            case TourOverlayPosition.TOP:
                return rect.top - GUTTER_DISTANCE
            case TourOverlayPosition.BOTTOM:
                return rect.bottom + GUTTER_DISTANCE
            case TourOverlayPosition.LEFT:
            case TourOverlayPosition.RIGHT:
                if (rect.top < GUTTER_DISTANCE) {
                    return rect.top + GUTTER_DISTANCE
                }

                return rect.top - 5
            default:
                return rect.top - 5
        }
    }

    const getLeftPosition = (): number => {
        switch (overlayPosition) {
            case TourOverlayPosition.LEFT:
                return rect.left - GUTTER_DISTANCE
            case TourOverlayPosition.RIGHT:
                return rect.right + GUTTER_DISTANCE
            case TourOverlayPosition.TOP:
            case TourOverlayPosition.BOTTOM:
                if (rect.left < GUTTER_DISTANCE) {
                    return rect.left + GUTTER_DISTANCE
                }

                return rect.left
            default:
                return rect.left
        }
    }

    const getTransform = (): string => {
        switch (overlayPosition) {
            case TourOverlayPosition.TOP:
                return 'translateY(-100%)'
            case TourOverlayPosition.LEFT:
                return 'translateX(-100%)'
            default:
                return ''
        }
    }

    const getMaxWidth = (): number => overlayPositionRect.width - GUTTER_DISTANCE * 2

    return (
        <>
            <button
                className='tour-exit'
                title={language.get(UserTour.UserTour_Exit)}
                onClick={(): void => cancelTour(true)}
            >
                <Icon iconName={IconName.X} />
            </button>
            <div
                className='tour-overlay'
                style={{
                    top: `${getTopPosition()}px`,
                    left: `${getLeftPosition()}px`,
                    transform: getTransform(),
                    maxWidth: `${getMaxWidth()}px`
                }}
                ref={ref}
            >
                <div className='to-content'>{content}</div>
                <div className='to-button-container'>
                    <WatsonButton onClick={(): void => cancelTour()}>
                        {language.get(UserTour.UserTour_Cancel)}
                    </WatsonButton>
                    {disableNext ? (
                        <></>
                    ) : (
                        <WatsonButton onClick={nextTour}>{language.get(UserTour.UserTour_Next)}</WatsonButton>
                    )}
                </div>
            </div>
        </>
    )
}
